import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { useAsyncMutation } from "~/pages/manifold-nextgen/hooks/useAsyncMutation";
import { apiMutate } from "~/utils/apiClient";

interface ConfirmDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** What will be lost, in the caller's words. Shown as the dialog's body. */
  description: React.ReactNode;
  confirmLabel: string;
  /** The resource path to `DELETE`. Null closes the dialog without a request. */
  path: string | null;
  errorMessage: string;
  onDeleted: () => void;
}

/**
 * Confirmation for the two destructive actions on this surface, which differ only
 * in their copy and their path. An `AlertDialog` rather than a `Dialog` because
 * both are irreversible: cancel takes the default, so a stray Return closes it.
 */
export const ConfirmDeleteDialog = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  path,
  errorMessage,
  onDeleted,
}: ConfirmDeleteDialogProps) => {
  const { execute, isPending } = useAsyncMutation(
    async () => {
      if (!path) {
        return null;
      }
      return apiMutate<null>(path, "DELETE", undefined, { expects204: true });
    },
    {
      errorMessage,
      onSuccess: () => {
        onDeleted();
        onOpenChange(false);
      },
    }
  );

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-sm text-muted-foreground">{description}</div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            // Kept out of the Radix auto-close so the dialog stays open on a
            // failed request, with the error toast visible against it.
            onClick={(event) => {
              event.preventDefault();
              void execute();
            }}
            disabled={isPending}
            className="bg-destructive text-white hover:bg-destructive/90"
          >
            {isPending ? "Deleting…" : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
